import { useEffect } from "react";
import {
  debounceTrailing as debounce,
  getCssTransformObj,
  getPercentToPixel,
  getPixelToPercent,
  normalizeAngle,
  rotatePoint,
} from "../_helpers/utils";
import {
  TYPE_PROPOSAL,
  TYPE_INFOGRAPHIC,
  TEXT,
  VIDEO,
  STOCK,
  BRAND,
  UPLOAD_VIDEO,
  GROUP_WIDGET,
  PICTURE,
  UPLOAD,
} from "../constants/editor";
import useVideo from "./useVideo";

const scrollContainerQuery = {
  preview: ".preview-content.customScroll",
  present: ".present-content",
};

const useResponsivePreview = ({ source, documentType, setMuted, deviceType, reloadParent, realDevice, published }) => {
  let pageLoadIntervalId;
  let scrollContainer;
  let sections;
  let desktopHtml = "";
  let blockWiseSortedGroups = {};
  let lastDeviceType = "desktop";
  let resetMediaPlayer = false;
  const { videoPlayer } = useVideo();

  const highlightEachGroup = groups => {
    groups.forEach((group, index) => {
      group.style.outline = `2px solid hsl(${index * 60}, 70%, 50%)`; // Different color for each group
    });
  };

  const playAllVideos = () => {
    setMuted(true); // due to browser default policy any video will not play with volume after window resize, so making the mute button icon as unmute via props backward drilling method in the preview.js
    document.querySelectorAll("video")?.forEach(video => {
      const widget = video.closest(".dhp-widgets");
      if (widget) videoPlayer({ event: "PLAY", widgetNode: widget, videoNode: video });
    });
  };

  const getOffsetRect = element => {
    const rect = JSON.parse(JSON.stringify(element.getBoundingClientRect()));
    return {
      ...rect,
      width: element.offsetWidth,
      height: element.offsetHeight,
      right: rect.left + element.offsetWidth,
      bottom: rect.top + element.offsetHeight,
    };
  };

  const getLayoutCss = ({ blockId, groupIdx, group, lastGroupIdx, previewScale, bWidth, bHeight }) => {
    const { top: gTop, height: gHeight, width: gWidth, insetBlockBottom, exceededBlockBottom, theta } = group.widgetCss;
    // Note - apply reverse scale for better readability (it's opposite to preview scale to nullify the decreasing readability effect upto certain level)
    // Step 1: Calculate basic reverse scale
    let reverseScale = 1 / previewScale;

    // Step 2: Ensure child does not exceed 80% of the parent
    const maxScaleForWidth = (0.85 * bWidth) / gWidth;
    const maxScaleForHeight = (0.85 * bHeight) / gHeight;
    reverseScale = Math.min(reverseScale, maxScaleForWidth, maxScaleForHeight);

    // Step 3: Ensure child reaches towards 75% of the parent’s width (if window with is decreases, and vice versa)
    const minWidthPercent = 0.45; // Minimum widget width in percent
    const maxWidthPercent = 0.8; // Maximum widget width in percent
    const minWindow = 320; // Minimum window width (e.g., mobile)
    const maxWindow = 768; // Maximum window width (e.g., Tab)
    // const windowWidth = window.innerWidth;
    const windowWidth = published
      ? window.innerWidth
      : deviceType === "MOBILE"
      ? 390
      : deviceType === "TABLET"
      ? 768
      : window.innerWidth;

    let dynamicWidthPercent =
      maxWidthPercent + ((windowWidth - minWindow) / (maxWindow - minWindow)) * (minWidthPercent - maxWidthPercent);

    dynamicWidthPercent =
      dynamicWidthPercent < minWidthPercent
        ? minWidthPercent
        : dynamicWidthPercent > maxWidthPercent
        ? maxWidthPercent
        : dynamicWidthPercent;

    const minScaleForWidth = (dynamicWidthPercent * bWidth) / gWidth;
    reverseScale = Math.max(reverseScale, minScaleForWidth);

    // Step 4: if widget width is greater than 80% of parent, set the reverse scale to 1 (default) as they are already in good readability condition
    reverseScale = gWidth > 0.8 * bWidth ? 1 : reverseScale;

    // calculate group widget height, margin top, margin bottom and layout height

    const layoutHeight = gHeight * reverseScale;

    const { bottom: prevGroupBottom = 0 } =
      blockWiseSortedGroups[blockId]?.sortedGroups?.[groupIdx - 1]?.widgetCss ?? {};

    const distanceFromPrevGroup =
      (groupIdx === 0 ? gTop : gTop > prevGroupBottom ? Math.abs(gTop - prevGroupBottom) : 40) * 1; // 1 is default reverse scale for marginTop because scale effect changes widget height in bottom (not in top)

    const distanceFromBlockBottom =
      (groupIdx === lastGroupIdx ? (exceededBlockBottom > 0 ? 0 : insetBlockBottom) : 0) * reverseScale; // scale effect changes widget height in bottom, so applying reverse scale for marginBottom (but applicable only to last widget)

    const layoutHeightWithMargin =
      layoutHeight +
      distanceFromPrevGroup +
      (groupIdx === lastGroupIdx ? (exceededBlockBottom > 0 ? -exceededBlockBottom : insetBlockBottom) : 0) *
        reverseScale; // here layoutHeightWithMargin includes layoutHeight + distanceFromPrevGroup + distanceFromBlockBottom, but substracting if exceededBlockBottom, otherwise adding insetBlockBottom (but applicable only to last widget)

    return {
      layoutHeight,
      layoutMarginTop: distanceFromPrevGroup,
      layoutMarginBottom: distanceFromBlockBottom,
      layoutHeightWithMargin,
      reverseScale,
      theta,
    };
  };

  const getResponsiveGroupOrders = ({ block, groups }) => {
    const blockRect = getOffsetRect(block);
    const widgetData = groups.map(widget => {
      const widgetCss = getWidgetCss({
        widget,
        parentCss: blockRect,
        styleUnitsAlreadyApplied: {
          width: "px",
          height: "px",
          left: "percent",
          top: "percent",
          right: "percent",
          bottom: "percent",
        },
        styleUnitsToFetch: {
          width: "px",
          height: "px",
          left: "px",
          top: "px",
          right: "px",
          bottom: "px",
        },
      });

      return {
        widget,
        widgetCss,
      };
    });

    // Sorting logic => sort by lowest top first then lowest left but comparing top lowset to bottom of next
    const sortedWidgets = widgetData
      .slice()
      .sort((a, b) => a.widgetCss.top - b.widgetCss.top)
      .sort((a, b) => {
        if (a.widgetCss.left < b.widgetCss.left && a.widgetCss.top < b.widgetCss.bottom) {
          return -1; // Keep current element first
        } else {
          return 1; // Swap positions otherwise
        }
      });

    return sortedWidgets;
  };

  const getWidgetCss = ({ widget, parentCss, styleUnitsAlreadyApplied = {}, styleUnitsToFetch = {} }) => {
    let { width = "", height = "", left = "", top = "", transform = "" } = widget.style ?? {};
    const { translate: { x: transX = "", y: transY = "" } = {}, rotate: { theta = 0 } = {} } = getCssTransformObj({
      transform,
    });

    width = parseFloat(width);
    height = parseFloat(height);
    left = parseFloat(!(left && top) ? transX : left);
    top = parseFloat(!(left && top) ? transY : top);

    width =
      styleUnitsToFetch.width === "px" && styleUnitsAlreadyApplied.width === "px"
        ? width
        : getPercentToPixel({
            parent: parentCss.width,
            child: width,
          });

    height =
      styleUnitsToFetch.height === "px" && styleUnitsAlreadyApplied.height === "px"
        ? height
        : getPercentToPixel({
            parent: parentCss.height,
            child: height,
          });

    left =
      styleUnitsToFetch.left === "px" && styleUnitsAlreadyApplied.left === "px"
        ? left
        : getPercentToPixel({
            parent: parentCss.width,
            child: left,
          });

    top =
      styleUnitsToFetch.top === "px" && styleUnitsAlreadyApplied.top === "px"
        ? top
        : getPercentToPixel({
            parent: parentCss.height,
            child: top,
          });

    const right = left + width;
    const bottom = top + height;
    let exceededBlockBottom = 0;
    let insetBlockBottom = 0;

    if (parentCss) {
      exceededBlockBottom = bottom > parentCss.height ? Math.abs(bottom - parentCss.height) : 0;
      insetBlockBottom = bottom < parentCss.height ? Math.abs(bottom - parentCss.height) : 0;
    }

    return {
      theta: parseFloat(theta),
      width,
      height,
      left,
      top,
      right,
      bottom,
      exceededBlockBottom,
      insetBlockBottom,
    };
  };

  const setWidgetCss = ({ parentCss, childCss, childNode, applyTheta, styleUnitsToApply = {}, additionalCss = {} }) => {
    childNode.style.width =
      styleUnitsToApply.width === "px"
        ? `${childCss.width}px`
        : `${getPixelToPercent({
            parent: parentCss.width,
            child: childCss.width,
          })}%`;

    childNode.style.height =
      styleUnitsToApply.height === "px"
        ? `${childCss.height}px`
        : `${getPixelToPercent({
            parent: parentCss.height,
            child: childCss.height,
          })}%`;

    childNode.style.left =
      styleUnitsToApply.left === "px"
        ? `${childCss.left}px`
        : `${getPixelToPercent({
            parent: parentCss.width,
            child: childCss.left,
          })}%`;

    childNode.style.top =
      styleUnitsToApply.top === "px"
        ? `${childCss.left}px`
        : `${getPixelToPercent({
            parent: parentCss.height,
            child: childCss.top,
          })}%`;

    if (applyTheta) childNode.style.transform = `rotate(${childCss.theta}deg)`;

    Object.keys(additionalCss).forEach(styleKey => (childNode.style[styleKey] = additionalCss[styleKey]));
  };

  const makePhysicalGroups = ({ block, groups }) => {
    groups.forEach(group => {
      const block = group[0]?.block;
      const blockRect = group[0]?.blockRect;

      if (group.length > 1) {
        // if more than one widget in a logical group
        const groupWrapper = document.createElement("div");
        const groupCss = {
          left: null,
          right: null,
          top: null,
          bottom: null,
        };

        // un-group pre-existing group-children (if any) and merge them in group
        const newGroupArray = [];
        group.forEach(widgetObj => {
          if (widgetObj.widget.dataset.assetType === GROUP_WIDGET) {
            // ungrouping
            const groupWidget = widgetObj.widget;
            const {
              translate: { x: groupWidgetTransX, y: groupWidgetTransY },
              rotate: { theta: groupWidgetTheta },
            } = getCssTransformObj({
              transform: groupWidget.style.transform,
            });

            const cssObj = {
              width: parseFloat(groupWidget.style.width),
              height: parseFloat(groupWidget.style.height),
              left: parseFloat(groupWidgetTransX),
              top: parseFloat(groupWidgetTransY),
            };

            Array.from(widgetObj.widget.querySelectorAll(".dhp-widgets")).forEach(widget => {
              // convert % to px and replace left, top with transform
              const {
                rotate: { theta: widgetTheta },
              } = getCssTransformObj({
                transform: widget.style.transform,
              });

              const theta = normalizeAngle(parseInt(widgetTheta) + parseInt(groupWidgetTheta));

              let childCssObj;
              let childWidgetDia = {
                width: getPercentToPixel({ parent: cssObj.width, child: widget.style.width }),
                height: getPercentToPixel({ parent: cssObj.height, child: widget.style.height }),
              };
              const widgetLeftPx = getPercentToPixel({ parent: cssObj.width, child: widget.style.left });
              const widgetTopPx = getPercentToPixel({ parent: cssObj.height, child: widget.style.top });
              const widgetCenterX = parseFloat(groupWidgetTransX) + widgetLeftPx + childWidgetDia.width / 2;
              const widgetCenterY = parseFloat(groupWidgetTransY) + widgetTopPx + childWidgetDia.height / 2;
              const groupCenterX = parseFloat(groupWidgetTransX) + cssObj.width / 2;
              const groupCenterY = parseFloat(groupWidgetTransY) + cssObj.height / 2;
              const rotatedCenter = rotatePoint(
                widgetCenterX,
                widgetCenterY,
                groupCenterX,
                groupCenterY,
                parseInt(groupWidgetTheta)
              );
              const newLeft = rotatedCenter.x - childWidgetDia.width / 2;
              const newTop = rotatedCenter.y - childWidgetDia.height / 2;

              const widgetTransformStr = getCssTransformObj({
                translateX: `${newLeft}px`,
                translateY: `${newTop}px`,
                rotateAngle: `${theta}deg`,
                transform: widget.style.transform,
                returnStr: true,
              });

              childCssObj = {
                width: `${childWidgetDia.width}px`,
                height: `${childWidgetDia.height}px`,
                transform: widgetTransformStr,
              };

              // apply additional data attributes
              widget.dataset.class = "dhp-root-widgets";

              // apply css
              widget.style.top = "";
              widget.style.left = "";
              widget.style.width = childCssObj.width;
              widget.style.height = childCssObj.height;
              widget.style.transform = childCssObj.transform;

              const widgetCss = getWidgetCss({
                widget,
                styleUnitsAlreadyApplied: {
                  width: "px",
                  height: "px",
                  left: "px",
                  top: "px",
                  right: "px",
                  bottom: "px",
                },
                styleUnitsToFetch: {
                  width: "px",
                  height: "px",
                  left: "px",
                  top: "px",
                  right: "px",
                  bottom: "px",
                },
              });

              newGroupArray.push({
                block,
                blockRect,
                widget,
                widgetCss,
              });
            });

            groupWidget.remove();
          } else {
            newGroupArray.push(widgetObj);
          }
        });

        newGroupArray.forEach(widgetObj => {
          const widgetCss = widgetObj.widgetCss;
          if (!groupCss.top || widgetCss.top < groupCss.top) groupCss.top = widgetCss.top;
          if (!groupCss.left || widgetCss.left < groupCss.left) groupCss.left = widgetCss.left;
          if (!groupCss.right || widgetCss.right > groupCss.right) groupCss.right = widgetCss.right;
          if (!groupCss.bottom || widgetCss.bottom > groupCss.bottom) groupCss.bottom = widgetCss.bottom;
        });

        groupCss.width = groupCss.right - groupCss.left;
        groupCss.height = groupCss.bottom - groupCss.top;

        newGroupArray.forEach(widgetObj => {
          const widget = widgetObj.widget;
          const clonedWidget = widget.cloneNode(true);
          setWidgetCss({
            parentCss: groupCss,
            childCss: {
              ...widgetObj.widgetCss,
              left: widgetObj.widgetCss.left - groupCss.left,
              top: widgetObj.widgetCss.top - groupCss.top,
            },
            childNode: clonedWidget,
            applyTheta: true,
            styleUnitsToApply: {
              width: "percent",
              height: "percent",
              left: "percent",
              top: "percent",
            },
          });
          groupWrapper.appendChild(clonedWidget);
          // remove each child widget from DOM after appending to runtime group widget
          widget.remove();
        });

        groupWrapper.dataset.assetType = "groups";
        groupWrapper.classList.add("runtime-physical-group");
        groupWrapper.classList.add("runtime-logical-group-widget");
        setWidgetCss({
          parentCss: {
            width: blockRect.width,
            height: blockRect.height,
          },
          childCss: groupCss,
          childNode: groupWrapper,
          applyTheta: false,
          styleUnitsToApply: {
            width: "px",
            height: "px",
            left: "percent",
            top: "percent",
          },
          additionalCss: {
            display: "block",
            position: "absolute",
            zIndex: 50,
            opacity: 1,
          },
        });

        block.appendChild(groupWrapper);
      } else {
        // if only one widget in a logical group (single widgets)
        group.forEach(widgetObj => {
          const widget = widgetObj.widget;
          widget.classList.add("runtime-physical-group");
          widget.classList.add("runtime-logical-single-widget");
          setWidgetCss({
            parentCss: {
              width: blockRect.width,
              height: blockRect.height,
            },
            childCss: widgetObj.widgetCss,
            childNode: widget,
            applyTheta: true,
            styleUnitsToApply: {
              width: "px",
              height: "px",
              left: "percent",
              top: "percent",
            },
          });
        });
      }
    });

    const physicalGroups = Array.from(block.querySelectorAll(".runtime-physical-group"));
    return physicalGroups;
  };

  const calculateMinimumDistance = ({ w1, w2 }) => {
    // Calculate horizontal and vertical gaps
    const horizontalDistance = Math.max(0, Math.max(w1.left, w2.left) - Math.min(w1.right, w2.right));
    const verticalDistance = Math.max(0, Math.max(w1.top, w2.top) - Math.min(w1.bottom, w2.bottom));

    // Calculate the minimum distance
    const distance = Math.sqrt(horizontalDistance ** 2 + verticalDistance ** 2);
    return distance;
  };

  const isOverlapping = ({ w1, w2 }) => {
    return w1.right > w2.left && w1.left < w2.right && w1.bottom > w2.top && w1.top < w2.bottom;
  };

  const getLogicalGroups = ({ block, threshold = 30 } = {}) => {
    const blockRect = getOffsetRect(block);
    const widgets = Array.from(block.querySelectorAll(`[data-class="dhp-root-widgets"]`));
    const widgetData = widgets.map(widget => {
      const widgetCss = getWidgetCss({
        widget,
        styleUnitsAlreadyApplied: {
          width: "px",
          height: "px",
          left: "px",
          top: "px",
          right: "px",
          bottom: "px",
        },
        styleUnitsToFetch: {
          width: "px",
          height: "px",
          left: "px",
          top: "px",
          right: "px",
          bottom: "px",
        },
      });

      return {
        block,
        blockRect,
        widget,
        widgetCss,
      };
    });

    const groupsByOverlap = [];
    const groupsByOverlapWithCss = [];
    const groupsByClosest = [];
    const visited = new Set();

    // detecting the group widgets by overlapping
    for (let i = 0; i < widgetData.length; i++) {
      if (visited.has(i)) continue;
      const group = [widgetData[i]];
      visited.add(i);

      for (let j = 0; j < widgetData.length; j++) {
        if (i === j || visited.has(j)) continue;
        const overlapping = isOverlapping({ w1: widgetData[i].widgetCss, w2: widgetData[j].widgetCss });

        if (overlapping) {
          group.push(widgetData[j]);
          visited.add(j);
        }
      }
      groupsByOverlap.push(group);
    }

    // determining the overlapping group widgets bundaries
    groupsByOverlap.forEach(group => {
      const groupCss = {
        left: null,
        right: null,
        top: null,
        bottom: null,
      };

      group.forEach(widgetObj => {
        const widgetCss = widgetObj.widgetCss;
        if (!groupCss.top || widgetCss.top < groupCss.top) groupCss.top = widgetCss.top;
        if (!groupCss.left || widgetCss.left < groupCss.left) groupCss.left = widgetCss.left;
        if (!groupCss.right || widgetCss.right > groupCss.right) groupCss.right = widgetCss.right;
        if (!groupCss.bottom || widgetCss.bottom > groupCss.bottom) groupCss.bottom = widgetCss.bottom;
      });

      groupCss.width = groupCss.right - groupCss.left;
      groupCss.height = groupCss.bottom - groupCss.top;

      groupsByOverlapWithCss.push({
        groupCss,
        widgets: group,
      });
    });

    // determining the final group widgets by minimum distance threshold value
    visited.clear();

    for (let i = 0; i < groupsByOverlapWithCss.length; i++) {
      if (visited.has(i)) continue;
      let group = groupsByOverlapWithCss[i].widgets;
      visited.add(i);

      for (let j = 0; j < groupsByOverlapWithCss.length; j++) {
        if (i === j || visited.has(j)) continue;
        const dist = calculateMinimumDistance({
          w1: groupsByOverlapWithCss[i].groupCss,
          w2: groupsByOverlapWithCss[j].groupCss,
        });

        if (dist < threshold) {
          group = [...group, ...groupsByOverlapWithCss[j].widgets];
          visited.add(j);
        }
      }
      groupsByClosest.push(group);
    }

    return groupsByClosest;
  };

  const generatePreResponsivePreview = () => {
    const blocks = Array.from(document.querySelectorAll(".section"));
    blocks.forEach(block => {
      const page = block.closest(".preview-page");
      const blockRect = getOffsetRect(block);
      const logicalGroups = getLogicalGroups({ block });
      const physicalGroups = makePhysicalGroups({ block, groups: logicalGroups });
      const sortedGroups = getResponsiveGroupOrders({ block, groups: physicalGroups });
      blockWiseSortedGroups[block.id] = { pageId: page.id, blockRect, sortedGroups };

      // Note: to be enabled for debugging purpose only
      // highlightEachGroup(physicalGroups);
    });
  };

  const generateResponsivePreview = firstRender => {
    let docHeight = 0;
    const previewBox = document.getElementById("preview-box");
    const previewOverflow = document.getElementById("preview-overflow");
    const previewOverflowWrap = document.getElementById("preview-overflow-wrap");
    const previewPageDOM = document.getElementsByClassName("preview-page")?.[0];
    // const screenWidth = window.innerWidth;
    const screenWidth = published
      ? window.innerWidth
      : deviceType === "MOBILE"
      ? 390
      : deviceType === "TABLET"
      ? 768
      : window.innerWidth;
    // const docWidth = parseFloat(document.querySelector(".preview-page figure").style.width) + 120;
    const docWidth = parseFloat(document.querySelector(".preview-page figure").style.width) + 0;
    // let previewScale = docWidth > screenWidth ? screenWidth / docWidth : 1;
    let previewScale = screenWidth / docWidth;
    const translateX = (screenWidth - (docWidth * previewScale - 0)) / 2;
    // const translateX = (screenWidth - (docWidth * previewScale - 120)) / 2;
    previewOverflow.style.transform = `scale(${previewScale}) translateX(${translateX}px)`;

    if (screenWidth <= 768) {
      // mobile and tab (responsive preview mechanism)
      if (lastDeviceType === "desktop") {
        generatePreResponsivePreview();
        lastDeviceType = "mobile";
        resetMediaPlayer = true;
      }

      const pages = Array.from(document.querySelectorAll(".preview-page"));
      pages.forEach(page => {
        let pageHeight = 0;
        Object.keys(blockWiseSortedGroups).forEach(blockId => {
          if (blockWiseSortedGroups[blockId].pageId === page.id) {
            let blockHeight = 0;
            const lastGroupIdx = blockWiseSortedGroups[blockId].sortedGroups.length - 1;
            const { height: bHeight, width: bWidth } = blockWiseSortedGroups[blockId].blockRect;
            const blockNode = document.getElementById(blockId);
            const container = document.createElement("div");
            container.classList.add("responsive-preview-mobile-container");

            blockWiseSortedGroups[blockId].sortedGroups.forEach((group, groupIdx) => {
              const { layoutHeight, layoutMarginTop, layoutMarginBottom, layoutHeightWithMargin, reverseScale, theta } =
                getLayoutCss({
                  blockId,
                  groupIdx,
                  group,
                  lastGroupIdx,
                  previewScale,
                  bWidth,
                  bHeight,
                });

              // adjust font size
              let childWidgets = Array.from(group.widget.querySelectorAll(`[data-class="dhp-root-widgets"]`));
              const isGroupWidget = childWidgets.length > 0;

              let increasedGroupHeightDeltaInPx = 0;
              let childNewTops = [];

              if (isGroupWidget) {
                // for group widget
                // Sorting logic => sort by lowest top first then lowest left but comparing top lowset to bottom of next
                childWidgets = childWidgets
                  .slice()
                  .sort((a, b) => parseFloat(a.style.top) - parseFloat(b.style.top))
                  .sort((a, b) => {
                    if (
                      parseFloat(a.style.left) < parseFloat(b.style.left) &&
                      parseFloat(a.style.top) < parseFloat(b.style.bottom)
                    ) {
                      return -1; // Keep current element first
                    } else {
                      return 1; // Swap positions otherwise
                    }
                  });

                childWidgets.forEach(widget => {
                  childNewTops.push({
                    widget,
                    newTop:
                      getPercentToPixel({
                        parent: group.widgetCss.height,
                        child: parseFloat(widget.style.top),
                      }) + increasedGroupHeightDeltaInPx, // Save absolute top before resizinggroupIdx
                  });

                  if (widget.dataset.assetType === TEXT) {
                    const textNode = widget.querySelector(".primary-text-widget");
                    const originalFontSize = parseFloat(textNode.dataset.originalFontSize || textNode.style.fontSize);
                    textNode.dataset.originalFontSize = originalFontSize; // storing originalFontSize for future reference
                    textNode.style.fontSize = `${originalFontSize / (previewScale * reverseScale * 1.45)}px`; // calculate final adjusted font size
                    textNode.style.overflowWrap = "";

                    // adjust the parent div height to fit text properly
                    const oldNewWidgetHeightInPx = getPercentToPixel({
                      parent: group.widgetCss.height,
                      child: parseFloat(widget.style.height),
                    });
                    const newWidgetHeightInPx = textNode.scrollHeight;
                    increasedGroupHeightDeltaInPx += newWidgetHeightInPx - oldNewWidgetHeightInPx;

                    widget.style.width = "auto";
                    widget.style.height = "auto"; // reset height first
                    widget.style.height = `${newWidgetHeightInPx}px`;
                    widget.style.top = `${getPercentToPixel({
                      parent: group.widgetCss.height,
                      child: parseFloat(widget.style.top),
                    })}px`;

                    // adjustment for text justifications
                    const saceleFactor = widget.offsetWidth / textNode.scrollWidth;
                    textNode.style.transform = `scale(${saceleFactor})`;
                    textNode.style.transformOrigin = "left";
                  } else {
                    widget.style.height =
                      widget.style.height === "100%"
                        ? "100%"
                        : `${getPercentToPixel({
                            parent: group.widgetCss.height,
                            child: parseFloat(widget.style.height),
                          })}px`;

                    widget.style.top = `${getPercentToPixel({
                      parent: group.widgetCss.height,
                      child: parseFloat(widget.style.top),
                    })}px`;
                  }

                  // adjustment for image and video widgets (if available height is 100%) to streatch the inner elements to 100%
                  if (
                    widget.style.height === "100%" &&
                    ((widget.dataset.assetType === VIDEO &&
                      [STOCK, BRAND, UPLOAD_VIDEO].includes(widget.dataset.source)) ||
                      [PICTURE, UPLOAD].includes(widget.dataset.assetType))
                  ) {
                    const flippable = widget.querySelector(".flippable");
                    const widgetInner = widget.querySelector(".dhp-widget-inner");
                    const videoNode = widget.querySelector("video");
                    flippable.style.height = "100%";
                    flippable.style.width = "auto";
                    widgetInner.style.height = "100%";
                    widgetInner.style.width = "auto";
                    widgetInner.style.transform = "";
                    if (videoNode) videoNode.style.objectFit = "cover";
                  }
                });
              } else {
                // for single widget
                const widget = group.widget;

                if (widget.dataset.assetType === TEXT) {
                  const textNode = widget.querySelector(".primary-text-widget");
                  const originalFontSize = parseFloat(textNode.dataset.originalFontSize || textNode.style.fontSize);
                  textNode.dataset.originalFontSize = originalFontSize; // storing originalFontSize for future reference
                  textNode.style.fontSize = `${originalFontSize / (previewScale * reverseScale * 1.45)}px`; // calculate final adjusted font size
                  textNode.style.overflowWrap = "";

                  // adjust the parent div height to fit text properly
                  const oldWidgetHeightInPx = parseFloat(widget.style.height);
                  const newWidgetHeightInPx = textNode.scrollHeight;
                  increasedGroupHeightDeltaInPx = newWidgetHeightInPx - oldWidgetHeightInPx;

                  widget.style.width = "auto"; // reset width to expand to auto width
                  widget.style.height = "auto"; // reset height to auto gain height
                  widget.style.height = `${newWidgetHeightInPx}px`;

                  // adjustment for text justifications
                  const saceleFactor = widget.offsetWidth / textNode.scrollWidth;
                  textNode.style.transform = `scale(${saceleFactor})`; // apply scale (scale-down) to autofit in text container
                  textNode.style.transformOrigin = "left";
                }
              }

              const newGroupHeight = group.widgetCss.height + increasedGroupHeightDeltaInPx;

              // adjust group style as per responsive view
              group.widget.style.top = "";
              group.widget.style.left = "";
              group.widget.style.position = "relative";
              group.widget.style.transform = `scale(${reverseScale}) rotate(${theta})`;
              group.widget.style.height = `${newGroupHeight}px`;

              // re-calculate top for group children
              if (isGroupWidget) {
                for (let i = 0; i < childNewTops.length; i++) {
                  let { widget, newTop } = childNewTops[i];
                  let newTopPercent = (newTop / newGroupHeight) * 100;
                  widget.style.top = newTopPercent + "%";
                }
              }

              // calculate new layout height (after font adjustments) and new layout height with margins (top and bottom)
              const newLayoutHeight = newGroupHeight * reverseScale;
              const layoutMarginHeight = layoutHeightWithMargin - layoutHeight; // layoutMarginHeight includes top and bottom margins
              const newLayoutHeightWithMargin = newLayoutHeight + layoutMarginHeight;

              // insert group to responsive layout
              const box = document.createElement("div");
              box.classList.add("responsive-preview-mobile-box");
              box.style.height = `${newLayoutHeight}px`;
              box.style.marginTop = `${layoutMarginTop}px`;
              box.style.marginBottom = `${layoutMarginBottom}px`;
              box.appendChild(group.widget);
              container.appendChild(box);

              blockHeight += newLayoutHeightWithMargin;
            });

            if (blockHeight === 0) blockHeight = bHeight;

            // remove un-necessary elements from DOM before appending responsive container
            blockNode
              .querySelectorAll(".runtime-physical-group, .responsive-preview-mobile-container")
              .forEach(element => element.remove());

            blockNode.appendChild(container);
            blockNode.style.height = `${blockHeight}px`; // apply block height
            pageHeight += blockHeight; // store page height
          }
        });

        const pageFigure = page.querySelector("figure");
        pageFigure.style.height = `${pageHeight}px`; // apply page height
        docHeight += pageHeight; // store document height
      });
    } else {
      // desktop
      if (firstRender || lastDeviceType === "mobile") resetMediaPlayer = true;
      lastDeviceType = "desktop";

      previewOverflow.innerHTML = desktopHtml;
      const pages = Array.from(document.querySelectorAll(".preview-page"));
      pages.forEach(page => {
        const pageFigure = page.querySelector("figure");
        docHeight += parseFloat(pageFigure.style.height);
      });
    }

    const documentHeightScaled = docHeight * previewScale;
    // const documentPaddingBottomScaled = 120 * previewScale;
    const documentPaddingBottomScaled = 0 * previewScale;
    // previewBox.style.height = `${documentHeightScaled + documentPaddingBottomScaled}px`;
    previewBox.style.height = "";
    previewOverflow.style.height = `${docHeight}px`;
    previewOverflow.style.width = `${docWidth}px`;

    if (previewOverflowWrap) {
      previewOverflowWrap.style.height = `${documentHeightScaled + documentPaddingBottomScaled}px`;
      previewOverflowWrap.style.width = `${docWidth * previewScale}px`;
    }

    if (realDevice && published) {
      previewBox.style.height = `${documentHeightScaled}px`;
    }

    if (previewPageDOM) {
      previewPageDOM.style.margin = "0 0";
      previewPageDOM.style.borderRadius = "0";
    }

    if (firstRender) {
      // applicable only for first render
      previewBox.style.display = "flex";
      previewBox.style.visibility = "visible"; // display preview after all changes done
      previewOverflow.style.willChange = "transform"; // resolved background line issue in preview

      const widgets = Array.from(document.querySelectorAll(".dhp-widgets"));
      widgets.forEach(widget => {
        widget.style.backfaceVisibility = "";
        widget.querySelectorAll("img").forEach(img => img.setAttribute("draggable", false));
      });
    }

    // generate media player and play all videos
    if (resetMediaPlayer) {
      resetMediaPlayer = false;
      document.querySelectorAll(".media-player").forEach(player => player.remove()); // remove all media player before recreating
      setTimeout(() => {
        // genrerate media player and auto-play them
        playAllVideos();

        // reset media player width
        document.querySelectorAll(".media-player").forEach(player => {
          if (player.closest(".runtime-physical-group")) {
            player.style.width = "100%";
          }
        });
      }, 100);
    }
  };

  const handlePageLoad = () => {
    pageLoadIntervalId = setInterval(() => {
      sections = document.querySelectorAll(".section");
      scrollContainer = document.querySelector(scrollContainerQuery[source]);

      if (scrollContainer && sections.length > 0) {
        console.log("load");
        // page is loaded successfully
        clearInterval(pageLoadIntervalId);
        pageLoadIntervalId = null;

        // prerequisite for responsive preview mechanism
        desktopHtml = document.getElementById("preview-overflow").innerHTML;
        generateResponsivePreview(true);
      }
    }, 100);
  };

  const resizeHandler = debounce(() => {
    if (published) window.location.reload();
  }, 500);

  useEffect(() => {
    if (
      source &&
      reloadParent &&
      [TYPE_PROPOSAL, TYPE_INFOGRAPHIC].includes(documentType) &&
      ["MOBILE", "TABLET"].includes(deviceType)
    ) {
      reloadParent(); // Automatically reload parent when deviceType changes
      handlePageLoad();
      window.addEventListener("resize", resizeHandler);
    }

    return () => {
      if (
        source &&
        reloadParent &&
        [TYPE_PROPOSAL, TYPE_INFOGRAPHIC].includes(documentType) &&
        ["MOBILE", "TABLET"].includes(deviceType)
      ) {
        window.removeEventListener("resize", resizeHandler);
      }
    };
  }, [source, documentType, deviceType]);

  return {};
};

export default useResponsivePreview;
